import { Box, CircularProgress } from '@mui/material';
import CustomModal from '@components/CustomModal';
import LogRocketConsentModal from '@components/modals/LogRocketConsentModal';
import TutorialModal from '@components/modals/TutorialModal';
import OnboardingSpotlight from '@components/OnboardingSpotlight';
import NoOrganizationScreen from '@components/NoOrganizationScreen';
import { graphQlClient } from '@config/graphqlClient';
import { updateUserPreferences } from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import Layout from '@layouts/Layout';
import { updateUserPreferencesRequest, UserRoles, ConsentOption } from '@models/user.model';
import AreaDescription from '@pages/areas/description/description';
import AreaList from '@pages/areas/list';
import AuthPage from '@pages/auth';
import DiscrepanciesList from '@pages/discrepancies/discrepancies';
import DocumentsList from '@pages/documents';
import ExecutionsList from '@pages/executions/executions';
import FeedbackPage from '@pages/feedback/feedback';
import IntegrationsCallbackPage from '@pages/integrations/callback';
import IntegrationList from '@pages/integrations/integrations';
import LoginPage from '@pages/login';
import LogoutPage from '@pages/logout';
import OrganizationDescription from '@pages/organizations/description/description';
import OrganizationsPage from '@pages/organizations/list/organizations';
import PolicyDescription from '@pages/policies/description/description';
import PoliciesList from '@pages/policies/list';
import ProcessDescription from '@pages/processes/description/description';
import ProcessList from '@pages/processes/list';
import ProcessPrint from '@pages/processes/print/print';
import ProcessMap from '@pages/processMap';
import ServiceDescription from '@pages/services/description/description';
import ServiceList from '@pages/services/list';
import SettingsPage from '@pages/settings';
import SignupPage from '@pages/signup';
import SkipPage from '@pages/skip/skip';
import SubscriptionsPage from '@pages/subscriptions/subscriptions';
import UsersPage from '@pages/users';
import { setUserPreferences, setShowTutorial } from '@redux/reducers/user.reducer';
import { initializeLogRocket, consentOptionToFlags } from '@utils/initializeLogRocket';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './app/redux/hooks';
import { RootState } from './app/redux/store';
const BillingPage = React.lazy(() => import('@pages/billing/billing'));

type PrivateRouteProps =
  | (RouteProps & {
      component: React.ComponentType<any>;
      render?: never;
    })
  | (RouteProps & {
      render: (props: any) => JSX.Element;
      component?: never;
    });

const PrivateRoute: React.FC<PrivateRouteProps> = (props) => {
  const { isLoggedIn, organizations } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const { component: Component, render, ...rest } = props as any;

  // Redirect to auth if not logged in (before rendering Layout)
  if (!isLoggedIn) {
    if (window.location.pathname !== '/logout') {
      sessionStorage.setItem('redirectUrl', window.location.pathname + window.location.search);
    }
    return <Redirect to="/auth" />;
  }

  // Show loading state while organization data is being loaded
  // This prevents Layout from mounting with null organizationId
  if (!organizationId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show no organization screen if user has no organizations
  if (organizations && organizations.length === 0) {
    return <NoOrganizationScreen />;
  }

  return (
    <Layout>
      <Route
        {...rest}
        render={(routeProps) => {
          if (render) {
            return render(routeProps);
          }

          if (Component) {
            return <Component {...routeProps} />;
          }

          return null;
        }}
      />
    </Layout>
  );
};

class AbsoluteRedirect extends React.Component<{ to: string }> {
  componentDidMount() {
    window.location.href = this.props.to;
  }

  render() {
    return null;
  }
}

const AppContent: React.FC = () => {
  const { isLoggedIn, role, userId, email, preferences, showTutorial } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const { isImpersonating } = useAppSelector((state: RootState) => state.impersonate);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DEBUG = ['development', 'test'].includes(process.env.NODE_ENV);
  const localeLogRocket = useTranslation('logRocket');
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasCheckedTutorial, setHasCheckedTutorial] = useState(false);

  // Extract specific preferences to avoid re-renders when other preferences change
  const allowLogRocketRecording = preferences?.allowLogRocketRecording;
  // Support both new nested structure and legacy flat keys for backward compatibility
  const tutorialCompleted = preferences?.tutorial?.completed ?? preferences?.tutorialCompleted;
  const tutorialDismissed = preferences?.tutorial?.dismissed ?? preferences?.tutorialDismissed;

  // Check for existing consent after login and initialize LogRocket
  useEffect(() => {
    // Skip LogRocket initialization/re-identification while impersonating
    // LogRocket keeps the admin's identity from before impersonation started
    if (DEBUG || !isLoggedIn || !userId || !email || !role || isImpersonating) return;

    // ONLY initialize LogRocket if user explicitly accepted
    if (allowLogRocketRecording === 'identified') {
      const { allowRecording, allowIdentification } = consentOptionToFlags(allowLogRocketRecording);
      const userData = allowIdentification ? { userId, email, role } : undefined;

      initializeLogRocket(allowRecording, allowIdentification, userData).catch(error => {
        console.error('Failed to initialize LogRocket:', error);
      });
    } else if (allowLogRocketRecording === 'decline') {
      // User explicitly declined - do nothing, don't show modal, don't initialize
    } else {
      // Show modal if no valid preference is set (undefined, null, or old 'anonymous' value)
      setShowConsentModal(true);
    }
  }, [DEBUG, isLoggedIn, allowLogRocketRecording, userId, email, role, isImpersonating]);

  const handleConsent = async (option: ConsentOption) => {
    const previousConsent = preferences?.allowLogRocketRecording;

    // Save to Redux - initialize preferences if null
    const basePreferences = preferences || {};
    const userPreferences = {
      ...basePreferences,
      allowLogRocketRecording: option,
    };
    dispatch(setUserPreferences(userPreferences));

    // Save to backend
    try {
      await graphQlClient.request<updateUserPreferencesRequest>(updateUserPreferences, {
        input: {
          preferences: userPreferences,
        },
      });
    } catch (error) {
      console.error('Failed to save LogRocket consent to backend:', error);
    }

    setShowConsentModal(false);

    // If consent changed, reload page to ensure LogRocket state is properly reset
    // This prevents scenarios where LogRocket remains initialized/identified after switching to anonymous/decline
    if (previousConsent && previousConsent !== option) {
      window.location.reload();
      return;
    }

    // ONLY initialize LogRocket if user explicitly accepted
    // This only runs on first-time consent (when previousConsent is undefined/null)
    if (option === 'identified') {
      const { allowRecording, allowIdentification } = consentOptionToFlags(option);
      const userData = allowIdentification && userId && email && role
        ? { userId, email, role }
        : undefined;

      initializeLogRocket(allowRecording, allowIdentification, userData).catch(error => {
        console.error('Failed to initialize LogRocket:', error);
      });
    }
  };



  // Auto-trigger tutorial for first-time users
  useEffect(() => {
    if (!isLoggedIn || !organizationId || showTutorial || showConsentModal) {
      return;
    }

    // Auto-show tutorial if preferences is null OR if never completed and never dismissed
    // Trigger on /processes page OR root path (for users with zero processes)
    const isOnValidPath = location.pathname === '/processes' || location.pathname === '/';
    const shouldShowTutorial = (preferences === null || (!tutorialCompleted && !tutorialDismissed)) && isOnValidPath;

    if (shouldShowTutorial && !hasCheckedTutorial) {
      dispatch(setShowTutorial(true));
      setHasCheckedTutorial(true);
    }
  }, [isLoggedIn, organizationId, preferences, tutorialCompleted, tutorialDismissed, location.pathname, hasCheckedTutorial, showTutorial, showConsentModal]);

  return (
    <>
      {/* // TODO: Uncomment if scrollTop is still an issue */}
      {/* <ScrollTop /> */}
      <Switch>
        <Route exact path="/login" component={LoginPage} />

        <Route exact path="/signup" component={SignupPage} />

        {/* Redirect from /auth/code... to /processes if logged in */}
        <Route path="/auth/*">{isLoggedIn ? <Redirect to="/processes" /> : <AuthPage />}</Route>

        {/* Specific route for the authentication page */}
        <Route exact path="/auth">
          {isLoggedIn ? <Redirect to="/processes" /> : <AuthPage />}
        </Route>

        {/* We use this as a landing after enabling an integration (the flow opens in a new tab) */}
        <Route exact path="/integrations/callback">
          {<IntegrationsCallbackPage />}
        </Route>

        <Route exact path="/logout">
          <LogoutPage />
        </Route>

        {/* PrivateRoute for Dashboard and all other authenticated routes */}
        {role === UserRoles.SUPER_ADMIN && <PrivateRoute path="/organizations" component={OrganizationsPage} />}
        {role === UserRoles.SUPER_ADMIN && <PrivateRoute path="/subscriptions" component={SubscriptionsPage} />}
        {role === UserRoles.SUPER_ADMIN && <PrivateRoute path="/feedback" component={FeedbackPage} />}
        {[UserRoles.OWNER, UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute
            exact
            path="/billing"
            render={() => (
              <React.Suspense fallback={null}>
                <BillingPage />
              </React.Suspense>
            )}
          />
        )}
        {[UserRoles.OWNER, UserRoles.ADMIN, UserRoles.SUPER_ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/organization/:organizationId" component={OrganizationDescription} />
        )}
        {[UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN].includes(role as UserRoles) && (
          <PrivateRoute path="/users" component={UsersPage} />
        )}
        <PrivateRoute exact path="/areas" component={AreaList} />
        <PrivateRoute path="/areas/:areaId" component={AreaDescription} />
        <PrivateRoute exact path="/services" component={ServiceList} />
        <PrivateRoute path="/services/:serviceId" component={ServiceDescription} />
        <PrivateRoute exact path="/processes" component={ProcessList} />
        <PrivateRoute exact path="/processes/:processId/print" component={ProcessPrint} />
        <PrivateRoute path="/processes/:processId" component={ProcessDescription} />
        <PrivateRoute exact path="/policies" component={PoliciesList} />
        <PrivateRoute path="/policies/:policyId" component={PolicyDescription} />
        <PrivateRoute path="/documents" component={DocumentsList} />
        <PrivateRoute path="/processMap" component={ProcessMap} />
        <PrivateRoute path="/integrations" component={IntegrationList} />
        <PrivateRoute path="/discrepancies" component={DiscrepanciesList} />
        <PrivateRoute path="/executions" component={ExecutionsList} />
        <PrivateRoute path="/settings" component={SettingsPage} />
        <PrivateRoute path="/profile" component={SettingsPage} />

        {/* Skip page - standalone route without Layout wrapper for external links */}
        <Route exact path="/skip">
          <SkipPage />
        </Route>

        {/* Optionally handle the root URL explicitly */}
        <Route path="/">
          {isLoggedIn ? <Redirect to="/processes" /> : <AbsoluteRedirect to={process.env.REACT_APP_MARKETING_URL || '/'} />}
        </Route>
      </Switch>

      <TutorialModal
        isOpen={showTutorial || false}
        onClose={() => dispatch(setShowTutorial(false))}
      />

      <OnboardingSpotlight />

      <CustomModal
        isOpen={showConsentModal}
        onClose={() => {}}
        hideClose={true}
        showButtons={false}
        title={localeLogRocket['title']}
        maxWidth="600px">
        <LogRocketConsentModal
          onConsent={handleConsent}
          initialValue={preferences?.allowLogRocketRecording || 'identified'}
        />
      </CustomModal>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
